import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { readMe } from "@directus/sdk";
import { IS_PREVIEW } from "../config";
import directus from "../data/directusClient";
import { clearClassrooms } from "../data/classrooms";
import { clearContent } from "../data/contentCache";
import { DENIED, OFFLINE, problemOf } from "../data/problems";
import { readJson, remove, writeJson } from "../data/storage";

// Only the id and first name are remembered; never a token.
const LAST_USER = "last-user";

export class SignInError extends Error {
  constructor(reason) {
    super(reason);
    this.reason = reason; // "invalid" | "offline" | "unknown"
  }
}

const toUser = (me) => ({
  id: me.id,
  firstName: me.first_name || null,
  lastName: me.last_name || null,
  avatarId: me.avatar || null,
});

const readCurrentUser = async () =>
  toUser(
    await directus.request(
      readMe({ fields: ["id", "first_name", "last_name", "avatar"] })
    )
  );

const liveAuth = {
  async current() {
    try {
      // The access token lives only in memory, so a reopened app first swaps
      // the httpOnly refresh cookie for a new one.
      const refreshFailure = await directus.refresh().then(
        () => null,
        (error) => error
      );
      if (refreshFailure && problemOf(refreshFailure) === OFFLINE) {
        throw refreshFailure;
      }
      // Signed in means this app has its own access token. Another Directus
      // session in the same browser (the CMS admin) can make the user readable
      // without one, but the learner gateway needs the token, so that is
      // treated as signed out and the teacher signs in here.
      if (!(await directus.getToken())) return null;
      return await readCurrentUser();
    } catch (error) {
      // Reopened without a connection: keep the last teacher on this device
      // working with lessons already saved here.
      return problemOf(error) === OFFLINE ? readJson(LAST_USER, null) : null;
    }
  },
  async signIn(email, password) {
    try {
      await directus.login(email, password);
      return await readCurrentUser();
    } catch (error) {
      const problem = problemOf(error);
      if (problem === OFFLINE) throw new SignInError("offline");
      if (problem === DENIED || error?.response?.status === 400) {
        throw new SignInError("invalid");
      }
      throw new SignInError("unknown");
    }
  },
  async signOut() {
    try {
      await directus.logout();
    } catch {
      // Signing out on this device still happens.
    }
  },
};

const previewAuth = {
  current: async () => readJson(LAST_USER, null),
  async signIn(email, password) {
    if (!email || !password) throw new SignInError("invalid");
    return {
      id: "preview",
      firstName: "Preview",
      lastName: "Teacher",
      avatarId: null,
    };
  },
  signOut: async () => {},
};

const auth = IS_PREVIEW ? previewAuth : liveAuth;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({ status: "checking", user: null });

  useEffect(() => {
    let active = true;
    auth.current().then((user) => {
      if (!active) return;
      setState(
        user
          ? { status: "signed-in", user }
          : { status: "signed-out", user: null }
      );
    });
    return () => {
      active = false;
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const user = await auth.signIn(email.trim(), password);
    writeJson(LAST_USER, user);
    setState({ status: "signed-in", user });
  }, []);

  const userId = state.user?.id;
  const signOut = useCallback(async () => {
    await auth.signOut();
    if (userId) {
      clearContent(userId);
      clearClassrooms(userId);
    }
    remove(LAST_USER);
    setState({ status: "signed-out", user: null });
  }, [userId]);

  const value = useMemo(
    () => ({ ...state, signIn, signOut }),
    [state, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
